import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { ArrowLeft, Funnel, SpinnerGap } from '@phosphor-icons/react';
import DatePickerDMY from '../components/DatePickerDMY';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_MAP = { '12-5': 'allocation_12_5', 'vat': 'vat', 'net': 'net', 'expenses': 'expenses', 'reserve-10': 'reserve_10', 'social-insurance-10': 'social_insurance_10' };

export default function FinanceDetail() {
  const { type: urlType } = useParams();
  const type = TYPE_MAP[urlType] || urlType;
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const axCfg = { withCredentials: true };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Phase 128 — Client filter removed from bucket-ledger pages (they are not client-based).
  // Replaced with Type filter: all | income | expense.
  const [entryTypeFilter, setEntryTypeFilter] = useState('all');
  const [categoryId, setCategoryId] = useState('all');
  const [categories, setCategories] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  const pageTitle = {
    allocation_12_5: `${t('allocation125')} – ${t('transactions')}`,
    vat: `${t('vatTotal')} – ${t('transactions')}`,
    net: `${t('availableBalance')} – ${t('transactions')}`,
    expenses: `${t('companyExpenses')} – ${t('transactions')}`,
    reserve_10: `${t('reserve10')} – ${t('transactions')}`,
    social_insurance_10: `${t('socialInsurance10')} – ${t('transactions')}`,
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (type === 'expenses') {
        const params = new URLSearchParams();
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
        if (categoryId !== 'all') params.append('category_id', categoryId);
        const [expR, catR, baR] = await Promise.all([
          axios.get(`${API}/company/expenses?${params}`, axCfg),
          axios.get(`${API}/company/expense-categories`, axCfg),
          axios.get(`${API}/bank-accounts`, axCfg),
        ]);
        setCategories(catR.data);
        setBankAccounts(baR.data);
        const rows = expR.data.map(e => ({
          ...e,
          entry_type: 'expense',
          amount: -Math.abs(e.amount),
          deduction_type: e.deduction_type || 'net',
        }));
        // Phase 216 — Sort by date with created_at tiebreaker (DESC default).
        rows.sort((a, b) => {
          const cmp = (b.date || '').localeCompare(a.date || '');
          if (cmp !== 0) return cmp;
          return (b.created_at || '').localeCompare(a.created_at || '');
        });
        const total = Math.round(rows.reduce((s, r) => s + r.amount, 0) * 100) / 100;
        let running = total;
        for (const r of rows) {
          r.balance_after = Math.round(running * 100) / 100;
          running = Math.round((running - r.amount) * 100) / 100;
        }
        setData({ type: 'expenses', total, count: rows.length, rows });
      } else {
        const params = new URLSearchParams({ type });
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
        if (categoryId !== 'all') params.append('category_id', categoryId);
        const [txnR, catR, baR] = await Promise.all([
          axios.get(`${API}/company/transactions?${params}`, axCfg),
          axios.get(`${API}/company/expense-categories`, axCfg),
          axios.get(`${API}/bank-accounts`, axCfg),
        ]);
        setCategories(catR.data);
        setBankAccounts(baR.data);
        // Phase 128 — Client-side entry type filter. Backend returns all rows; we trim to income/expense on demand.
        const allRows = txnR.data.rows || [];
        const rows = entryTypeFilter === 'all'
          ? allRows
          : allRows.filter(r => {
              // Treat expense/bank_payment as "expense", everything else (income, income_portion) as "income".
              const isExpense = r.entry_type === 'expense' || r.entry_type === 'bank_payment';
              return entryTypeFilter === 'expense' ? isExpense : !isExpense;
            });
        // Recompute running balances for the filtered view so the balance_after column stays coherent.
        // Phase 216 — Sort by date with created_at tiebreaker (DESC default).
        rows.sort((a, b) => {
          const cmp = (b.date || '').localeCompare(a.date || '');
          if (cmp !== 0) return cmp;
          return (b.created_at || '').localeCompare(a.created_at || '');
        });
        const total = Math.round(rows.reduce((s, r) => s + (r.amount || 0), 0) * 100) / 100;
        let running = total;
        for (const r of rows) {
          r.balance_after = Math.round(running * 100) / 100;
          running = Math.round((running - (r.amount || 0)) * 100) / 100;
        }
        setData({ ...txnR.data, rows, total, count: rows.length });
      }
    } catch { toast.error(t('errorOccurred')); }
    setLoading(false);
  }, [type, dateFrom, dateTo, entryTypeFilter, categoryId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmtDate = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };
  const getBankName = (id) => { const b = bankAccounts.find(x => x.account_id === id); return b ? b.name : ''; };
  const getCatName = (e) => language === 'el' ? (e.category_name_el || e.category_name_en || e.category || '') : (e.category_name_en || e.category || '');
  const bucketLabel = (dt) => ({ vat: t('bucketVat'), allocation_12_5: t('bucketAllocation'), net: t('bucketNet') }[dt] || t('bucketNet'));
  const bucketColor = (dt) => ({ vat: 'border-amber-200 bg-amber-50 text-amber-700', allocation_12_5: 'border-violet-200 bg-violet-50 text-violet-700', net: 'border-blue-200 bg-blue-50 text-blue-700' }[dt] || 'border-zinc-200 bg-zinc-50 text-zinc-600');

  // Phase 128 — Client filter removed on bucket-ledger pages; replaced with a Type filter (all/income/expense).
  // Expenses page: neither client nor type filter applies (everything is already an expense).
  const isBucketLedger = type === 'allocation_12_5' || type === 'vat' || type === 'net' || type === 'reserve_10' || type === 'social_insurance_10';
  const showTypeFilter = isBucketLedger;
  const showCategoryFilter = type === 'net' || type === 'expenses';

  return (
    <Layout>
      <div className="space-y-5" data-testid="finance-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/finance')} className="rounded-none" data-testid="back-to-finance">
            <ArrowLeft size={18} className="mr-1" /> {t('backToFinance')}
          </Button>
          <h1 className="text-xl font-bold tracking-tight">{pageTitle[type] || t('transactions')}</h1>
        </div>

        {/* Filters — From Date, To Date, Type (for bucket ledgers), Category (where applicable) */}
        <div className="flex flex-wrap items-center gap-2 border border-zinc-200 bg-white p-3">
          <DatePickerDMY value={dateFrom} onChange={setDateFrom} className="h-8 w-36 font-mono text-xs" data-testid="detail-filter-from" />
          <span className="text-zinc-400 text-xs">-</span>
          <DatePickerDMY value={dateTo} onChange={setDateTo} className="h-8 w-36 font-mono text-xs" data-testid="detail-filter-to" />
          {showTypeFilter && (
            <Select value={entryTypeFilter} onValueChange={setEntryTypeFilter}>
              <SelectTrigger className="rounded-none h-8 w-40 text-xs" data-testid="detail-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="filter-type-all">{t('allTypes')}</SelectItem>
                <SelectItem value="income" data-testid="filter-type-income">{t('companyIncome')}</SelectItem>
                <SelectItem value="expense" data-testid="filter-type-expense">{t('companyExpenses')}</SelectItem>
              </SelectContent>
            </Select>
          )}
          {showCategoryFilter && (
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="rounded-none h-8 w-40 text-xs" data-testid="detail-filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allCategories')}</SelectItem>
                {categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{language === 'el' ? (c.name_el || c.name_en) : c.name_en}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); setEntryTypeFilter('all'); setCategoryId('all'); }} className="rounded-none h-8 text-xs" data-testid="detail-filter-clear"><Funnel size={14} /></Button>
        </div>

        {/* Summary stripe */}
        {data && (
          <div className="flex gap-4">
            <div className="border border-zinc-200 bg-white p-4 flex-1">
              <div className="text-xs text-zinc-500 uppercase font-semibold tracking-wider">{t('total')}</div>
              <div className={`text-xl font-mono font-bold mt-1 ${data.total >= 0 ? 'text-emerald-800' : 'text-red-800'}`} data-testid="detail-total">&euro;{Math.abs(data.total).toFixed(2)}</div>
            </div>
            <div className="border border-zinc-200 bg-white p-4">
              <div className="text-xs text-zinc-500 uppercase font-semibold tracking-wider">{t('transactions')}</div>
              <div className="text-xl font-mono font-bold mt-1" data-testid="detail-count">{data.count}</div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16"><SpinnerGap size={32} className="animate-spin text-zinc-400" /></div>
          ) : !data || data.rows.length === 0 ? (
            <div className="text-center py-16 text-zinc-400" data-testid="detail-empty">{t('noTransactions')}</div>
          ) : (
            <table className="excel-table text-sm" data-testid="detail-table">
              <thead className="sticky top-0 bg-white z-10">
                <tr>
                  <th className="text-left">{t('date')}</th>
                  <th className="text-left">{t('incomeDescription')}</th>
                  {type === 'expenses' && <th className="text-left">{t('expenseCategory')}</th>}
                  {type === 'expenses' && <th className="text-left">{t('deductedFrom')}</th>}
                  {type === 'net' && <th className="text-left">{t('type')}</th>}
                  {(type === 'allocation_12_5' || type === 'vat' || type === 'reserve_10' || type === 'social_insurance_10') && <th className="text-left">{t('type')}</th>}
                  {(type === 'allocation_12_5' || type === 'vat' || type === 'reserve_10' || type === 'social_insurance_10') && <th className="text-left">{t('client')}</th>}
                  <th className="text-left">{t('paymentSource')}</th>
                  <th className="text-right">{t('amount')}</th>
                  <th className="text-right">{t('balanceAfter')}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={r.ref_id || r.expense_id || i} data-testid={`detail-row-${i}`} className={r.entry_type === 'expense' ? 'bg-red-50/30' : ''}>
                    <td className="font-mono">{fmtDate(r.date)}</td>
                    <td>{r.description}</td>
                    {type === 'expenses' && <td className="text-xs">{getCatName(r)}</td>}
                    {type === 'expenses' && <td className="text-xs"><span className={`px-2 py-0.5 border ${bucketColor(r.deduction_type)}`}>{bucketLabel(r.deduction_type)}</span></td>}
                    {type === 'net' && (
                      <td className="text-xs">
                        <span className={`px-2 py-0.5 border ${r.entry_type === 'income' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
                          {r.entry_type === 'income' ? t('companyIncome') : t('companyExpenses')}
                        </span>
                      </td>
                    )}
                    {(type === 'allocation_12_5' || type === 'vat' || type === 'reserve_10' || type === 'social_insurance_10') && (
                      <td className="text-xs">
                        <span className={`px-2 py-0.5 border ${r.entry_type === 'income' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
                          {r.entry_type === 'income' ? t('companyIncome') : t('companyExpenses')}
                        </span>
                      </td>
                    )}
                    {(type === 'allocation_12_5' || type === 'vat' || type === 'reserve_10' || type === 'social_insurance_10') && <td className="text-xs text-zinc-600">{r.category || '-'}</td>}
                    <td className="text-xs"><span className={`px-2 py-0.5 border ${r.source === 'bank' ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-green-200 bg-green-50 text-green-700'}`}>{r.source === 'bank' ? (getBankName(r.bank_account_id) || t('bankSource')) : t('cashSource')}</span></td>
                    <td className={`font-mono text-right font-semibold ${r.amount >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{r.amount >= 0 ? '+' : ''}&euro;{r.amount.toFixed(2)}</td>
                    <td className="font-mono text-right text-zinc-600">&euro;{r.balance_after.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="bg-zinc-50 font-semibold border-t-2 border-zinc-300">
                  <td colSpan={type === 'expenses' ? 6 : type === 'net' ? 5 : (type === 'allocation_12_5' || type === 'vat' || type === 'reserve_10' || type === 'social_insurance_10') ? 5 : 4} className="text-right">{t('total')}</td>
                  <td className={`font-mono text-right ${data.total >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>&euro;{data.total.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
